import { OverlappedPressureChart } from '../respiratory-muscle/RespiratoryMuscleCharts';
import AssessmentHistoryComparisonCard, {
  type AssessmentHistoryComparisonEntry,
} from '../components/AssessmentHistoryComparisonCard';
import {
  buildOverlappedPressureData,
  getRespiratorySeverityTone,
  summarizeSIndexPayload,
  type PlotWaveform,
  type SIndexResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import {
  getAssessmentInterpretationOverride,
  resolveAssessmentInterpretationText,
} from '../utils/interpretationOverride';

export const getSIndexSeverityLabel = (payload?: SIndexResultPayload | null, referencePimaxMax?: number | null): string =>
  summarizeSIndexPayload(payload, referencePimaxMax).severity;

export const getSIndexSeverityTone = (payload?: SIndexResultPayload | null, referencePimaxMax?: number | null): string =>
  getRespiratorySeverityTone(summarizeSIndexPayload(payload, referencePimaxMax).tone);

export const getSIndexInterpretationText = (payload?: SIndexResultPayload | null, referencePimaxMax?: number | null): string => {
  const summary = summarizeSIndexPayload(payload, referencePimaxMax);
  if (summary.maxPressure == null) return 'Não há valor válido de S-Index para interpretar.';
  const percentSegment = summary.percentPredicted != null
    ? `, correspondente a ${summary.percentPredicted.toFixed(1)}% do predito`
    : '';
  const comparisonSegment = summary.llnSIndex != null && summary.belowLLN != null
    ? summary.belowLLN
      ? `, abaixo do limite inferior esperado de ${summary.llnSIndex.toFixed(1)} cmH2O.`
      : `, dentro da faixa esperada (limite inferior de ${summary.llnSIndex.toFixed(1)} cmH2O).`
    : '. O limite inferior esperado não pôde ser determinado com os dados disponíveis.';
  const automaticInterpretation = `O S-Index medido foi de ${summary.maxPressure.toFixed(1)} cmH2O${percentSegment}${comparisonSegment}`;
  const professionalOverride = getAssessmentInterpretationOverride(payload);
  if (professionalOverride) return professionalOverride;
  const resolved = resolveAssessmentInterpretationText(
    payload,
    automaticInterpretation,
  );
  return resolved.includes('Resultado em relação ao valor previsto') ? automaticInterpretation : resolved;
};

interface SIndexResultRendererProps {
  payload: SIndexResultPayload | null | undefined;
  chartImages?: Record<string, string> | null;
  referencePimaxMax?: number | null;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

export default function SIndexResultRenderer({
  payload,
  chartImages = null,
  referencePimaxMax = null,
  assessmentDate = null,
  comparisonHistory = [],
}: SIndexResultRendererProps) {
  const summary = summarizeSIndexPayload(payload, referencePimaxMax);
  const plotBreaths: PlotWaveform[] = Array.isArray(payload?.plotBreaths) ? payload!.plotBreaths : [];
  const selectedBreaths = Array.isArray(payload?.selectedBreaths) && payload!.selectedBreaths.length ? payload!.selectedBreaths : plotBreaths.map((breath) => breath.breathNumber);
  const visibleBreaths = plotBreaths.filter((breath) => selectedBreaths.includes(breath.breathNumber));
  const pressureData = buildOverlappedPressureData(visibleBreaths);
  const isManualEntry = !summary.metrics.length && summary.measurements.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-slate-600">S-INDEX</p>
          <div className="text-6xl font-black leading-none text-slate-900">{summary.maxPressure != null ? summary.maxPressure.toFixed(0) : '-'}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">{summary.maxPressure != null ? 'cmH2O' : 'sem valor válido'}</div>
          <div className={`mt-4 inline-flex rounded-full px-4 py-1.5 text-xs font-bold shadow-sm ${getSIndexSeverityTone(payload, referencePimaxMax)}`}>{summary.severity}</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{getSIndexInterpretationText(payload, referencePimaxMax)}</p>
            <ul className="mt-3 space-y-1 text-xs leading-relaxed text-slate-500">
              {summary.interpretation.lines.filter((line) => !line.startsWith('S-Index ')).map((line, index) => <li key={`sindex-summary-line-${index}`}>{line}</li>)}
            </ul>
          </div>
          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Referência: <span className="font-semibold text-slate-900">{summary.referenceLabel}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              {isManualEntry ? 'Medidas registradas' : 'Respirações analisadas'}: <span className="font-semibold text-slate-900">{summary.breathsAnalyzed}</span>
            </div>
          </div>
        </div>
      </div>

      <AssessmentHistoryComparisonCard
        testKey="S_INDEX"
        currentPayload={payload}
        currentAssessmentDate={assessmentDate}
        history={comparisonHistory}
      />

      {(summary.predictedSIndex != null || summary.percentPredicted != null || summary.llnSIndex != null) && <div className={`grid gap-6 ${summary.llnSIndex != null ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {summary.predictedSIndex != null && <Metric label="Predito" value={`${summary.predictedSIndex.toFixed(1)} cmH2O`} />}
        {summary.percentPredicted != null && <Metric label="% do predito" value={`${summary.percentPredicted.toFixed(1)}%`} />}
        {summary.llnSIndex != null && <Metric label="Limite inferior esperado" value={`${summary.llnSIndex.toFixed(1)} cmH2O`} />}
      </div>}

      {chartImages?.pressure_overlapped ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Curvas de pressão sobrepostas</div>
          <img src={chartImages.pressure_overlapped} alt="Curvas de S-Index" className="mt-4 w-full rounded-2xl border border-slate-100" />
        </div>
      ) : visibleBreaths.length ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Curvas de pressão sobrepostas</div>
          <div className="mt-4">
            <OverlappedPressureChart data={pressureData} breaths={visibleBreaths} height={260} />
          </div>
        </div>
      ) : null}

      {summary.metrics.length ? (
        <div className="overflow-x-auto rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Resp.</th>
                <th className="px-4 py-3 text-left">Target</th>
                <th className="px-4 py-3 text-left">Tins</th>
                <th className="px-4 py-3 text-left">Pico pressão</th>
                <th className="px-4 py-3 text-left">Pico fluxo</th>
                <th className="px-4 py-3 text-left">Pico volume</th>
                <th className="px-4 py-3 text-left">Trabalho</th>
              </tr>
            </thead>
            <tbody>
              {summary.metrics.map((metric) => (
                <tr key={metric.breathNumber} className="border-t">
                  <td className="px-4 py-3">{metric.breathNumber}</td>
                  <td className="px-4 py-3">{metric.targetResistance != null ? metric.targetResistance.toFixed(1) : '-'}</td>
                  <td className="px-4 py-3">{metric.tins.toFixed(2)} s</td>
                  <td className="px-4 py-3">{metric.peakPressure.toFixed(2)} cmH2O</td>
                  <td className="px-4 py-3">{metric.peakFlow.toFixed(2)} L/s</td>
                  <td className="px-4 py-3">{metric.peakVolume.toFixed(2)} L</td>
                  <td className="px-4 py-3">{metric.totalWork.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : summary.measurements.length ? (
        <div className="overflow-x-auto rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Medida</th>
                <th className="px-4 py-3 text-left">S-Index</th>
              </tr>
            </thead>
            <tbody>
              {summary.measurements.map((value, index) => (
                <tr key={`sindex-measurement-${index + 1}`} className="border-t">
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3">{value.toFixed(1)} cmH2O</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
  </div>
);
